import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { Subject } from 'rxjs';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { ObjectTypes } from '@fi-shared/types';
import { ISimpleEditorAction } from '@fi-shared/fi-simple-contenteditable/fi-simple-contenteditable.d';
import { FiSimpleContenteditableComponent } from '@fi-app/shared/fi-simple-contenteditable/fi-simple-contenteditable.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiCommentsListStateService } from '../services/fi-comments-list-state.service';
import { FiCommentsListHelperService } from '../services/fi-comments-list-helper.service';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'fi-comments-list-item-edit',
    template: `
        <fi-simple-contenteditable
            [contentInputPlaceholder]="'text_editor.comment_tooltip' | fiAppText"
            [objectType]="ObjectTypes.Comment"
            [(content)]="comment.Content"
            [(mediaBankFileReferences)]="comment.MediaBankFileReferences"
            [(linkAttachments)]="comment.Links"
            [actionInsideEditor]="actionInsideEditor"
            [editorActions]="editorActions"
            [isAttachmentDividerShown]="false"
            [isTagUserFeatureEnabled]="true"
            [taggedUsers]="comment.TaggedUsers"
            [isEmbedVideoFeatureEnabled]="true"
            [isGifFeatureEnabled]="true"
        />
    `,
    styleUrls: ['./fi-comments-list-item-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-comments-list-item-edit' },
    standalone: false,
})
export class FiCommentsListItemEditComponent implements OnInit, OnDestroy {
    @Input({ required: true }) objectId: number;
    @Input({ required: true }) objectType: ObjectTypes;
    @Input({ required: true }) instanceId: string;
    @Input() comment: ICommentModel;
    @Input() saveButtonText: string;
    @Input() autofocus: boolean;

    @Input() isCancelButtonShown: boolean = true;
    @Input() isNewComment: boolean = false;

    @Output() closeEditor = new EventEmitter<void>();

    @ViewChild(FiSimpleContenteditableComponent, { static: true })
    simpleContenteditableComponent: FiSimpleContenteditableComponent;

    protected readonly ObjectTypes = ObjectTypes;
    protected editorActions: ISimpleEditorAction[] = [];
    protected actionInsideEditor: ISimpleEditorAction;
    constructor(
        private userDataService: FiUserDataService,
        private textEditorService: TextEditorService,
        private appTextService: AppTextService,
        private commentsListStateService: FiCommentsListStateService,
        private commentsListHelperService: FiCommentsListHelperService,
        private textEditorTagUsersService: TextEditorTagUsersService,
    ) {}

    public ngOnInit(): void {
        this.setEditorActions();
        this.setActionInsideEditor();
    }

    protected setActionInsideEditor(): void {
        if (!this.isNewComment) {
            return;
        }
        this.actionInsideEditor = {
            onClick: () => this.saveComment(),
            iconClass: 'fas fa-paper-plane-top',
            isDisabledFn: this.isSaveButtonDisabled.bind(this),
        };
    }

    protected setEditorActions(): void {
        if (this.isNewComment) {
            return;
        }
        this.editorActions = [
            {
                onClick: () => this.closeEditor.emit(),
                buttonText: this.appTextService.get('common.cancel'),
                buttonClass: 'fi-stroke-button-standard BrandedColor',
            },
            {
                onClick: () => this.saveComment(),
                buttonText: this.saveButtonText,
                buttonClass: 'fi-stroke-button-standard BrandedBackgroundColor BrandedBorderColor',
                isDisabledFn: this.isSaveButtonDisabled.bind(this),
            },
        ];
    }

    private isSaveButtonDisabled(): boolean {
        return (
            (!this.comment.Content?.trim().length || this.comment.Content === '<br>') &&
            !this.comment.MediaBankFileReferences?.length &&
            !this.comment.Links?.length
        );
    }

    protected saveComment(): void {
        let content = this.comment.Content || '';
        this.comment.Content = this.textEditorService.removeEmptyLinesFromEnd(content);
        if (!this.comment.Id) {
            const currentUser = this.userDataService.getUserData();
            this.comment.CreatedByUserId = currentUser.UserId;
            this.comment.CreatedByUserName = currentUser.FullName;
            this.comment.UserProfile = this.userDataService.getUserProfile();
            this.comment.CreatedOn = new Date().toISOString();
        } else {
            this.comment.ChangedOn = new Date().toISOString();
        }
        this.commentsListStateService.emitSaveComment({
            ObjectId: this.objectId,
            ObjectType: this.objectType,
            Comment: {
                ...this.comment,
                Content: this.textEditorTagUsersService.getConvertedFromEditToViewModeUserTags(this.comment.Content),
            },
            InstanceId: this.instanceId,
        });
        this.comment = this.commentsListHelperService.getNewCommentInstance(
            this.comment.ObjectId,
            this.comment.ObjectType,
            this.comment.ParentId,
        );
        this.closeEditor.emit();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
